import { useEffect, useState } from "react";
import { CopyButton } from "@/components/common/CopyButton";

type PlatformId = "unix" | "ps" | "wsl";

interface Platform {
  id: PlatformId;
  label: string;
  shortLabel: string;
  prompt: string;
  head: string;
  tail: string;
  tag: string;
}

const PLATFORMS: Record<PlatformId, Platform> = {
  unix: {
    id: "unix",
    label: "Linux / macOS",
    shortLabel: "Linux/macOS",
    prompt: "$",
    head: "curl -fsSL https://agentfly.net/install.sh | bash -s -- --token=",
    tail: "",
    tag: "curl · bash",
  },
  ps: {
    id: "ps",
    label: "Windows · PowerShell",
    shortLabel: "Windows · PS",
    prompt: "PS>",
    head: "$env:TOKEN='",
    tail: "'; irm https://agentfly.net/install.ps1 | iex",
    tag: "irm · iex",
  },
  wsl: {
    id: "wsl",
    label: "Windows · WSL",
    shortLabel: "Windows · WSL",
    prompt: "PS>",
    head: "$env:TOKEN='",
    tail: "'; irm https://agentfly.net/wsl.ps1 | iex",
    tag: "irm · WSL",
  },
};

interface Agent {
  slug: string;
  name: string;
  tagline: string;
  shortDesc: string;
  longDesc: string;
  version?: string;
  highlights: string[];
  platforms: PlatformId[];
  cover: string;
  rating?: number;
  ghost?: false;
}

interface GhostAgent {
  slug: string;
  name: string;
  tagline: string;
  shortDesc: string;
  cover: string;
  ghost: true;
}

const AGENTS: Array<Agent | GhostAgent> = [
  {
    slug: "hermes",
    name: "Hermes",
    tagline: "智能体研究框架",
    shortDesc: "自带 Python 与 Node 运行时，离线可用。无需折腾依赖。",
    longDesc:
      "Hermes 是 Agentfly 自研的智能体研究框架。安装包内置独立 Python / Node 运行时与一组常用依赖，安装到独立目录，与系统全局环境完全隔离。适合做智能体相关的本地实验与离线复现。",
    version: "v2026.5.7",
    highlights: ["内置 Python + Node 运行时", "完全离线可跑", "目录隔离，不污染系统", "默认配置已就绪"],
    platforms: ["unix"],
    cover: "/agents/hermes.png",
    rating: 4.6,
  },
  {
    slug: "openclaw",
    name: "openclaw",
    tagline: "开源命令行编程助手",
    shortDesc: "国内源直连，插件即装即用，飞书通道开箱可用。",
    longDesc:
      "openclaw 是一款开源命令行 coding agent。Agentfly 版本预置了国内源、飞书通道与默认 prompt，安装后续装插件不再走 npm 海外源，避免常见的网络与版本兼容问题。",
    version: "2026.5.7",
    highlights: ["国内 npm 源", "飞书通道开箱可用", "插件即装即用", "上游同步更新"],
    platforms: ["unix"],
    cover: "/agents/openclaw.png",
    rating: 4.7,
  },
  {
    slug: "claude",
    name: "Claude Code",
    tagline: "Anthropic 官方 CLI",
    shortDesc: "Anthropic 官方 Claude Code，附独立 Node 运行时。",
    longDesc:
      "Claude Code 是 Anthropic 出品的命令行 coding agent。Agentfly 版本附独立 Node 运行时与已配置好的模型通道，安装即可直接 claude --version 启动，无需手动 export ANTHROPIC_BASE_URL。",
    version: "2.1.141",
    highlights: ["官方一致", "通道已接通", "三平台覆盖", "独立 Node 运行时"],
    platforms: ["unix", "ps", "wsl"],
    cover: "/agents/claude.png",
    rating: 4.8,
  },
  {
    slug: "codex",
    name: "Codex",
    tagline: "OpenAI Codex CLI",
    shortDesc: "OpenAI Codex CLI，安装即装好必要环境与代理。",
    longDesc:
      "Codex 是 OpenAI 出品的命令行 coding agent。Agentfly 安装包预置了模型通道、代理设置与配置文件，无需手动配置 Python / Node、API Base URL 与代理，开箱即跑。",
    version: "0.130.0",
    highlights: ["环境自动", "通道已接通", "三平台覆盖", "国内代理已配"],
    platforms: ["unix", "ps", "wsl"],
    cover: "/agents/codex.png",
    rating: 4.7,
  },
  {
    slug: "wsl",
    name: "WSL",
    tagline: "Windows 子系统",
    shortDesc: "Windows 一键完成 WSL + Ubuntu 24.04 离线初始化。",
    longDesc:
      "Agentfly WSL 安装包内嵌已下载好的 Ubuntu 24.04 镜像，一行 PowerShell 命令即可启用 WSL 功能并完成镜像导入，告别 wsl --install --distribution 长时间拉取过程。",
    version: "2.6.3",
    highlights: ["内嵌 Ubuntu 24.04", "离线镜像", "免等系统拉取", "支持企业网络"],
    platforms: ["ps"],
    cover: "/agents/wsl.png",
    rating: 4.6,
  },
  {
    slug: "next",
    name: "下一批",
    tagline: "生产线持续上新",
    shortDesc: "想看到的智能体写信告诉我们 —— 优先级向真实需求倾斜。",
    cover: "/agents/next.png",
    ghost: true,
  },
];

interface AgentMarketProps {
  onOpenAuth: () => void;
}

export function AgentMarket({ onOpenAuth }: AgentMarketProps) {
  const [tokenAgent, setTokenAgent] = useState<Agent | null>(null);
  const [detailAgent, setDetailAgent] = useState<Agent | GhostAgent | null>(null);

  // Esc close
  useEffect(() => {
    if (!tokenAgent && !detailAgent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setTokenAgent(null);
        setDetailAgent(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tokenAgent, detailAgent]);

  // Body scroll lock when any modal open
  useEffect(() => {
    if (tokenAgent || detailAgent) {
      document.body.classList.add("market-modal-open");
      return () => document.body.classList.remove("market-modal-open");
    }
  }, [tokenAgent, detailAgent]);

  return (
    <section className="market" id="market">
      <div className="market-bg" aria-hidden="true" />
      <div className="container">
        <div className="market-header">
          <span className="section-label">
            <span className="market-section-num">01 /</span> 应用市场
          </span>
          <h2 className="section-title">
            挑一个智能体，<br />
            <span className="gradient-text">一行命令搬回家</span>
          </h2>
          <p className="section-desc">
            和应用商店一样，挑卡片就行 ——
            <br />
            点「获取授权码」拿一行安装命令，点卡片其它位置查看详情。
          </p>
        </div>

        <div className="market-cards">
          {AGENTS.map((a) => {
            const isGhost = "ghost" in a && a.ghost;
            return (
              <article
                key={a.slug}
                className={"market-card" + (isGhost ? " is-ghost" : "")}
                onClick={() => setDetailAgent(a)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailAgent(a);
                  }
                }}
                aria-label={`${a.name} —— ${a.tagline}，查看详情`}
              >
                <div className="market-card-cover">
                  <img src={a.cover} alt="" loading="lazy" />
                  {!isGhost && (
                    <span className="market-card-platforms" aria-hidden="true">
                      {a.platforms.map((p) => (
                        <PlatformGlyph key={p} id={p} />
                      ))}
                    </span>
                  )}
                  {isGhost && <span className="market-card-soon">敬请期待</span>}
                </div>
                <div className="market-card-body">
                  <div className="market-card-title-row">
                    <h3 className="market-card-name">{a.name}</h3>
                    {!isGhost && a.rating ? (
                      <span className="market-card-rating" aria-label={`评分 ${a.rating}`}>
                        <StarGlyph />
                        {a.rating.toFixed(1)}
                      </span>
                    ) : null}
                  </div>
                  <div className="market-card-tagline">{a.tagline}</div>
                  <p className="market-card-desc">{a.shortDesc}</p>
                </div>
                <div className="market-card-foot">
                  {!isGhost ? (
                    <>
                      <button
                        type="button"
                        className="market-card-cta"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setTokenAgent(a);
                        }}
                      >
                        获取授权码
                        <span className="market-card-cta-arrow" aria-hidden="true">→</span>
                      </button>
                      <span className="market-card-foot-meta">版本 {a.version}</span>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="market-card-cta is-soft"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        window.location.href = "mailto:hello@agentfly.net?subject=Agent%20%E6%8F%90%E5%90%8D";
                      }}
                    >
                      提名一个智能体
                      <span className="market-card-cta-arrow" aria-hidden="true">→</span>
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <p className="market-bottom-hint">
          还没有授权码？
          <a
            href="#console"
            onClick={(ev) => {
              ev.preventDefault();
              onOpenAuth();
            }}
          >
            登录控制台
          </a>
          一键创建。
        </p>
      </div>

      {tokenAgent ? (
        <TokenModal
          agent={tokenAgent}
          onClose={() => setTokenAgent(null)}
          onOpenAuth={onOpenAuth}
        />
      ) : null}
      {detailAgent ? (
        <DetailModal
          agent={detailAgent}
          onClose={() => setDetailAgent(null)}
          onGetToken={(a) => {
            setDetailAgent(null);
            setTokenAgent(a);
          }}
        />
      ) : null}
    </section>
  );
}

/* -------- 「获取授权码」modal -------- */

function TokenModal({
  agent,
  onClose,
  onOpenAuth,
}: {
  agent: Agent;
  onClose: () => void;
  onOpenAuth: () => void;
}) {
  const platforms = agent.platforms;
  return (
    <div className="market-modal" role="dialog" aria-modal="true" aria-label={`${agent.name} 获取授权码`}>
      <div className="market-modal-backdrop" onClick={onClose} />
      <div className="market-modal-card market-modal-token" onClick={(e) => e.stopPropagation()}>
        <header className="market-modal-head">
          <div>
            <span className="market-modal-eyebrow">一键安装</span>
            <h3>获取 {agent.name} 授权码</h3>
            <p>把 <code>&lt;TOKEN&gt;</code> 换成你的授权码，复制到终端回车即可。</p>
          </div>
          <button className="market-modal-close" type="button" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="market-modal-cmds">
          {platforms.map((pid) => {
            const p = PLATFORMS[pid];
            const fullCmd = `${p.head}<TOKEN>${p.tail}`;
            return (
              <div className="market-cmd-row" key={pid}>
                <div className="market-cmd-row-head">
                  <span className="market-cmd-icon"><PlatformGlyph id={pid} /></span>
                  <div>
                    <div className="market-cmd-label">{p.label}</div>
                    <div className="market-cmd-tag">{p.tag}</div>
                  </div>
                </div>
                <div className="market-cmd-box">
                  <pre>
                    <code>
                      <span className="terminal-prompt">{p.prompt}</span>{" "}
                      {p.head}
                      <span className="market-token">&lt;TOKEN&gt;</span>
                      {p.tail}
                    </code>
                  </pre>
                  <CopyButton text={fullCmd} className="market-cmd-copy install-copy" />
                </div>
              </div>
            );
          })}
        </div>

        <footer className="market-modal-foot">
          <span>
            还没有授权码？
            <a
              href="#console"
              onClick={(ev) => {
                ev.preventDefault();
                onClose();
                onOpenAuth();
              }}
            >
              登录控制台
            </a>
            一键创建。
          </span>
          <button className="market-modal-secondary" type="button" onClick={onClose}>
            关闭
          </button>
        </footer>
      </div>
    </div>
  );
}

/* -------- 智能体详情 modal（二级 tab） -------- */

type DetailTab = "overview" | "platforms" | "install";

function DetailModal({
  agent,
  onClose,
  onGetToken,
}: {
  agent: Agent | GhostAgent;
  onClose: () => void;
  onGetToken: (a: Agent) => void;
}) {
  const isGhost: boolean = "ghost" in agent && agent.ghost === true;
  const [tab, setTab] = useState<DetailTab>("overview");
  const tabs: Array<{ id: DetailTab; label: string }> = isGhost
    ? [{ id: "overview", label: "概览" }]
    : [
        { id: "overview", label: "概览" },
        { id: "platforms", label: "平台与版本" },
        { id: "install", label: "一键安装命令" },
      ];

  return (
    <div className="market-modal market-modal-detail-wrap" role="dialog" aria-modal="true" aria-label={`${agent.name} 详情`}>
      <div className="market-modal-backdrop" onClick={onClose} />
      <div className="market-modal-card market-modal-detail" onClick={(e) => e.stopPropagation()}>
        <button className="market-modal-close" type="button" aria-label="关闭" onClick={onClose}>
          ×
        </button>

        <header className="market-detail-hero">
          <img src={agent.cover} alt="" />
          <div className="market-detail-hero-meta">
            <h3>{agent.name}</h3>
            <p>{agent.tagline}</p>
          </div>
        </header>

        <nav className="market-detail-tabs" role="tablist" aria-label="智能体详情">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              className={"market-detail-tab" + (tab === t.id ? " is-active" : "")}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="market-detail-body">
          {tab === "overview" && (
            <DetailOverview agent={agent} isGhost={isGhost} />
          )}
          {!isGhost && tab === "platforms" && <DetailPlatforms agent={agent as Agent} />}
          {!isGhost && tab === "install" && <DetailInstall agent={agent as Agent} />}
        </div>

        {!isGhost ? (
          <footer className="market-detail-foot">
            <button
              type="button"
              className="market-card-cta"
              onClick={() => onGetToken(agent as Agent)}
            >
              获取授权码
              <span className="market-card-cta-arrow" aria-hidden="true">→</span>
            </button>
            <button className="market-modal-secondary" type="button" onClick={onClose}>
              关闭
            </button>
          </footer>
        ) : (
          <footer className="market-detail-foot">
            <a
              className="market-card-cta is-soft"
              href="mailto:hello@agentfly.net?subject=Agent%20%E6%8F%90%E5%90%8D"
            >
              提名一个智能体
              <span className="market-card-cta-arrow" aria-hidden="true">→</span>
            </a>
            <button className="market-modal-secondary" type="button" onClick={onClose}>
              关闭
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function DetailOverview({ agent, isGhost }: { agent: Agent | GhostAgent; isGhost: boolean }) {
  if (isGhost) {
    return (
      <div className="market-detail-section">
        <h4>下一批智能体正在打磨</h4>
        <p>{agent.shortDesc}</p>
        <p>
          Agentfly 把常用 AI 工具打包成更容易上手的入口，让小白也能复制即用。
          想看到的智能体直接写信，优先级会向真实需求倾斜。
        </p>
      </div>
    );
  }
  const a = agent as Agent;
  return (
    <div className="market-detail-section">
      <h4>{a.tagline}</h4>
      <p>{a.longDesc}</p>
      <ul className="market-detail-highlights">
        {a.highlights.map((h) => (
          <li key={h}>
            <CheckGlyph />
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DetailPlatforms({ agent }: { agent: Agent }) {
  const allIds: PlatformId[] = ["unix", "ps", "wsl"];
  return (
    <div className="market-detail-section">
      <h4>支持的平台</h4>
      <div className="market-detail-platforms">
        {allIds.map((pid) => {
          const supported = agent.platforms.includes(pid);
          const p = PLATFORMS[pid];
          return (
            <div
              key={pid}
              className={"market-detail-platform" + (supported ? " is-on" : " is-off")}
            >
              <PlatformGlyph id={pid} />
              <div>
                <div className="market-detail-platform-name">{p.label}</div>
                <div className="market-detail-platform-tag">
                  {supported ? p.tag : "暂未支持"}
                </div>
              </div>
              <span className="market-detail-platform-state">
                {supported ? "✓" : "—"}
              </span>
            </div>
          );
        })}
      </div>
      <dl className="market-detail-meta">
        <div>
          <dt>版本</dt>
          <dd>
            <code>{agent.version}</code>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function DetailInstall({ agent }: { agent: Agent }) {
  return (
    <div className="market-detail-section">
      <h4>各平台一键安装命令</h4>
      <p className="market-detail-hint">把命令里的 <code>&lt;TOKEN&gt;</code> 换成你的授权码，再复制粘贴到终端。</p>
      <div className="market-modal-cmds">
        {agent.platforms.map((pid) => {
          const p = PLATFORMS[pid];
          const cmd = `${p.head}<TOKEN>${p.tail}`;
          return (
            <div className="market-cmd-row" key={pid}>
              <div className="market-cmd-row-head">
                <span className="market-cmd-icon"><PlatformGlyph id={pid} /></span>
                <div>
                  <div className="market-cmd-label">{p.label}</div>
                  <div className="market-cmd-tag">{p.tag}</div>
                </div>
              </div>
              <div className="market-cmd-box">
                <pre>
                  <code>
                    <span className="terminal-prompt">{p.prompt}</span>{" "}
                    {p.head}
                    <span className="market-token">&lt;TOKEN&gt;</span>
                    {p.tail}
                  </code>
                </pre>
                <CopyButton text={cmd} className="market-cmd-copy install-copy" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------- glyphs -------- */

function PlatformGlyph({ id }: { id: PlatformId }) {
  if (id === "unix") {
    return (
      <svg className="market-glyph" viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M8 1.5c-2.2 0-3.6 1.7-3.6 3.5 0 1.6.6 2.5.6 3.6 0 1.1-1.5 1.9-1.5 3.4 0 1.6 1.5 2.5 4.5 2.5s4.5-.9 4.5-2.5c0-1.5-1.5-2.3-1.5-3.4 0-1.1.6-2 .6-3.6 0-1.8-1.4-3.5-3.6-3.5z"
          fill="currentColor"
          opacity="0.9"
        />
        <circle cx="6.6" cy="5.4" r="0.7" fill="#0a0a0f" />
        <circle cx="9.4" cy="5.4" r="0.7" fill="#0a0a0f" />
      </svg>
    );
  }
  if (id === "ps") {
    return (
      <svg className="market-glyph" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="1" y="2" width="14" height="12" rx="1.5" fill="currentColor" opacity="0.9" />
        <path
          d="M3.5 5.5l2 2-2 2M6.5 9.5h3"
          stroke="#0a0a0f"
          strokeWidth="1.2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg className="market-glyph" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1" y="2" width="6" height="5.5" fill="currentColor" opacity="0.9" />
      <rect x="8" y="2" width="7" height="5.5" fill="currentColor" opacity="0.7" />
      <rect x="1" y="8.5" width="6" height="5.5" fill="currentColor" opacity="0.7" />
      <rect x="8" y="8.5" width="7" height="5.5" fill="currentColor" opacity="0.9" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        d="M3 8.5l3 3 7-7"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path
        d="M8 1.5l1.95 4.2 4.6.5-3.4 3.1.95 4.55L8 11.6 3.9 13.85l.95-4.55-3.4-3.1 4.6-.5L8 1.5z"
        fill="currentColor"
      />
    </svg>
  );
}
